import React, { useEffect, useMemo, useState } from "react";
import { formatTeacherTime, teacherApi } from "./teacherApi";

type Batch = {
  id: number;
  name: string;
  courseTitle?: string | null;
  startDate?: string | null;
  startTime?: string | null;
  days?: string | null;
  seats?: number | null;
  enrolledStudents?: number | null;
  status?: string | null;
};

const generateUpcomingDays = () => {
  const dates = [];
  const today = new Date();
  for (let i = 0; i < 14; i++) {
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + i);
    dates.push(nextDate);
  }
  return dates;
};

export default function TeacherSchedule() {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  
  const upcomingDays = useMemo(() => generateUpcomingDays(), []);

  useEffect(() => {
    teacherApi.myBatches().then(setBatches).finally(() => setLoading(false));
  }, []);

  const scheduleForDay = useMemo(() => {
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const selectedDayName = dayNames[selectedDate.getDay()];
    const selectedDateString = selectedDate.toISOString().slice(0, 10);

    const classesToday = batches.filter((batch) => {
      if (batch.startDate && String(batch.startDate).slice(0, 10) > selectedDateString) {
        return false;
      }
      const batchDays = (batch.days || "").toLowerCase();
      if (batchDays.includes(selectedDayName.toLowerCase())) return true;
      if (batchDays.includes("mon - sat") && selectedDate.getDay() >= 1 && selectedDate.getDay() <= 6) return true;
      if (batchDays.includes("daily")) return true;
      return false;
    });

    return classesToday.sort((a, b) => {
      return (a.startTime || "00:00").localeCompare(b.startTime || "00:00");
    });
  }, [batches, selectedDate]);

  const isToday = selectedDate.toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6 max-w-5xl font-['Inter']">
      <header className="rounded-[30px] bg-white p-6 shadow-sm border border-slate-200">
        <p className="text-xs font-black uppercase tracking-wide text-blue-600">My Schedule</p>
        <h2 className="mt-2 text-3xl font-black text-slate-950">Daily Timetable</h2>
        <p className="mt-2 text-sm font-bold text-slate-500">
          Select a date to view your scheduled live classes for that day.
        </p>
      </header>

      {/* 📌 HORIZONTAL DATE SELECTOR (SCROLLBAR HIDDEN & CLEAN) */}
      <section className="rounded-[30px] bg-white p-5 shadow-sm border border-slate-200">
        <div 
          className="flex gap-3 overflow-x-auto pb-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} // Hide scrollbar for Firefox & IE
        >
          <style>{`
            div::-webkit-scrollbar {
              display: none; /* Hide scrollbar for Chrome, Safari and Opera */
            }
          `}</style>
          {upcomingDays.map((date, idx) => {
            const isSelected = date.toISOString().slice(0, 10) === selectedDate.toISOString().slice(0, 10);
            const dayName = date.toLocaleDateString("en-IN", { weekday: "short" });
            const dateNum = date.getDate();
            const isCurrentToday = date.toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10);

            return (
              <button
                key={idx}
                onClick={() => setSelectedDate(date)}
                className={`flex min-w-[78px] flex-col items-center justify-center rounded-2xl py-3.5 transition-all ${
                  isSelected
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-600/25 scale-105"
                    : "bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-900 border border-slate-100"
                }`}
              >
                <span className={`text-[10px] font-black uppercase ${isSelected ? "text-blue-100" : "text-slate-400"}`}>
                  {isCurrentToday ? "Today" : dayName}
                </span>
                <span className="mt-1 text-xl font-black">{dateNum}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* 📌 VERTICAL TIMELINE FOR SELECTED DATE */}
      <section className="rounded-[30px] bg-white p-6 sm:p-8 shadow-sm border border-slate-200">
        <div className="mb-8 flex items-center justify-between">
          <h3 className="text-xl font-black text-slate-950">
            {isToday ? "Today's Schedule" : selectedDate.toLocaleDateString("en-IN", { weekday: 'long', month: 'long', day: 'numeric' })}
          </h3>
          <span className="rounded-full bg-blue-50 px-4 py-1.5 text-xs font-black text-blue-700">
            {scheduleForDay.length} Classes
          </span>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm font-bold text-slate-400">Loading schedule...</div>
        ) : scheduleForDay.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-slate-50 py-12">
            <span className="text-4xl mb-3">☕</span>
            <p className="text-base font-black text-slate-900">You are off duty!</p>
            <p className="text-sm font-bold text-slate-500 mt-1">No classes are scheduled for this date.</p>
          </div>
        ) : (
          <div className="relative border-l-2 border-slate-100 ml-4 pl-6 sm:ml-6 sm:pl-8 space-y-8">
            {scheduleForDay.map((batch, idx) => (
              <div key={`${batch.id}-${idx}`} className="relative">
                <div className="absolute -left-[35px] sm:-left-[43px] top-1.5 h-4 w-4 rounded-full border-4 border-white bg-blue-600 shadow-sm" />
                
                <div className="rounded-[24px] border border-slate-100 bg-[#f8fbff] p-5 shadow-sm transition hover:shadow-md hover:border-blue-200">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="rounded-lg bg-blue-100 px-2.5 py-1 text-[10px] font-black uppercase text-blue-700">
                          {formatTeacherTime(batch.startTime || "")}
                        </span>
                        <span className="rounded-lg bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase text-emerald-700 border border-emerald-100">
                          {batch.status}
                        </span>
                      </div>
                      <h4 className="text-xl font-black text-slate-950">{batch.name}</h4>
                      <p className="mt-1 text-sm font-bold text-slate-500">{batch.courseTitle}</p>
                    </div>

                    <div className="flex flex-row sm:flex-col gap-2 shrink-0">
                      <button 
                        onClick={() => alert(`Connecting to live room for ${batch.name}...`)}
                        className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition"
                      >
                        Go Live 🎥
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 flex items-center gap-6 border-t border-slate-200/60 pt-4">
                    <div>
                      <p className="text-[10px] font-black uppercase text-slate-400">Class Type</p>
                      <p className="text-sm font-black text-slate-900 mt-0.5">Online Lecture</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase text-slate-400">Students</p>
                      <p className="text-sm font-black text-blue-700 mt-0.5">{batch.enrolledStudents || 0} Expected</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}